/**
 * Non-dismissable warning modal shown when the inactivity countdown
 * reaches the WARNING phase. The user has 60 seconds to click "Stay
 * signed in" before `useInactivityLogout` fires `onLogout`.
 *
 * Rendered unconditionally inside `<AuthenticatedLayout>` but only
 * `opened` when `phase === "warning"` — Mantine handles the mount/unmount
 * cleanly.
 *
 * @agentic connections
 */

import { Button, Group, Modal, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconClockExclamation } from "@tabler/icons-react";

/** Props for InactivityWarningModal. @agentic connections */
export type InactivityWarningModalProps = {
  opened: boolean;
  secondsUntilLogout: number;
  onExtendSession: () => void;
};

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/** Formats a countdown as e.g. "8 minutes 48 seconds", "1 minute", "42 seconds". */
const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return plural(seconds, "second");
  if (seconds === 0) return plural(minutes, "minute");
  return `${plural(minutes, "minute")} ${plural(seconds, "second")}`;
};

export const InactivityWarningModal = ({
  opened,
  secondsUntilLogout,
  onExtendSession,
}: InactivityWarningModalProps) => (
  <Modal
    opened={opened}
    onClose={() => {
      // Modal is non-dismissable — closing must go through the
      // explicit "Stay signed in" button. Ignore Mantine's onClose
      // (which fires from the X button, escape key, or click-outside,
      // all of which we disable below).
    }}
    closeOnClickOutside={false}
    closeOnEscape={false}
    withCloseButton={false}
    centered
    // Sit above any open app modal (encounter, patient, task, …) which use
    // Mantine's default modal z-index (200). Without this, a warning that
    // fires while a modal is open renders beneath it and is unreachable.
    zIndex={1000}
    size="md"
    radius="lg"
    // Fade in like the boot splash so the warning surfaces smoothly rather
    // than snapping in over whatever's underneath.
    transitionProps={{
      transition: "fade",
      duration: 220,
      timingFunction: "ease-out",
    }}
    // Deep-purple, blurred backdrop matching the brand boot-splash backdrop.
    overlayProps={{ color: "#1e1b3a", backgroundOpacity: 0.55, blur: 4 }}
    title={
      <Group gap="sm">
        <ThemeIcon
          variant="gradient"
          gradient={{ from: "purple.5", to: "purple.8", deg: 135 }}
          radius="xl"
          size="lg"
        >
          <IconClockExclamation size={20} />
        </ThemeIcon>
        <Text fw={700} size="lg">
          Still there?
        </Text>
      </Group>
    }
    aria-labelledby="inactivity-warning-title"
  >
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        You'll be signed out in{" "}
        <Text span fw={700} c="purple.7">
          {formatCountdown(secondsUntilLogout)}
        </Text>{" "}
        due to inactivity. Your unsaved changes will be preserved in this tab.
      </Text>
      <Group justify="flex-end">
        <Button
          variant="gradient"
          gradient={{ from: "purple.6", to: "purple.8", deg: 135 }}
          radius="md"
          onClick={onExtendSession}
          aria-label="Stay signed in"
        >
          Stay signed in
        </Button>
      </Group>
    </Stack>
  </Modal>
);
